import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { actionQueries } from '@/features/actions/queries'
import {
	AutomationSettingsFormSkeleton,
	AutomationSettingsNewSkeleton,
} from '@/features/air/automation-settings/components/skeletons'
import { policyQueries } from '@/features/air/policy-management/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'

const AutomationSettingsForm = lazy(
	() => import('@/features/air/automation-settings/components/form'),
)

const searchSchema = z.object({
	mapped_id: z.string().optional(),
	automation_id: z.string().optional(),
	automation_type: z.enum(['action', 'playbook']).optional(),
})

export const Route = createFileRoute('/_dashboard/air/automation-settings/new')(
	{
		validateSearch: zodValidator(searchSchema),

		beforeLoad: () => roleAuthGuard(ROLES.Analyst),

		loader: ({ context: { queryClient } }) => {
			const tenant_id = getCurrentTenant()

			if (tenant_id) {
				// Prefetch list of policies
				queryClient.prefetchQuery(policyQueries.policies_list(tenant_id))

				// Prefetch list of actions
				queryClient.prefetchQuery(actionQueries.list(tenant_id))

				// Prefetch list of playbooks
				queryClient.prefetchQuery(playbookQueries.list(tenant_id))
			}
		},

		head: () => ({
			meta: [
				{
					title: 'Create New Automation | Air | BitLyft AIR®',
				},
			],
		}),
		component: RouteComponent,
		pendingComponent: AutomationSettingsNewSkeleton,
		errorComponent: RouteErrorComponent,
	},
)

function RouteComponent() {
	const search = Route.useSearch()

	return (
		<Section>
			<div className="flex items-start justify-start gap-4">
				<BackLink to="/air/automation-settings" />

				<SectionTitle>Create New Automation</SectionTitle>
			</div>

			<div className="flex w-full flex-col items-start justify-start gap-2">
				<ErrorBoundary>
					<Suspense fallback={<AutomationSettingsFormSkeleton />}>
						<AutomationSettingsForm initialMappedId={search.mapped_id} />
					</Suspense>
				</ErrorBoundary>
			</div>
		</Section>
	)
}
